import type { CatalogImportOptionValueRequest } from '@/api/handlers/resources/catalogImports';
import type {
    AttributeType,
    CatalogImportLineResult,
    CatalogImportNameResult,
    CatalogImportNamesResult,
    CatalogImportVatType,
    ProductUnit,
} from '@/api/models';
import {
    targetKeyOf,
    targetOfKey,
    type NameOption,
} from '@/pages/catalogImport/names';
import { comparableText } from '@/pages/catalogImport/parse';
import type {
    NamesDecisions,
    ParsedLine,
    ParsedLinePatch,
} from '@/pages/catalogImport/store';
import { TEMPLATE_UNITS } from '@/pages/catalogImport/template';

/**
 * The drawer's form for one row: what the fields show, as text and picker
 * keys. A picker key is an entity's id, or `text:` and a text of the file, as
 * the Names step keys them (`targetKeyOf`).
 */
export type RowDrawerForm = {
    // Product: one product per SKU, so these apply to every row of the group.
    name: string;
    categoryKey: string | null;
    brandKey: string | null;
    /** A `ProductUnit` name, or the file's text when it names no unit. */
    unit: string;
    /** `standard` or `exempt`, or the file's text when it names neither. */
    vatType: string;
    // Variant: this row only.
    barcode: string;
    /** The value of each variation axis of the row's category, by attribute type id. */
    axisValues: Readonly<Record<string, string | null>>;
    salePrice: string;
    wholesalePrice: string;
    minimumQuantity: string;
    // Batch: this row only.
    lotNumber: string;
    quantity: string;
    cost: string;
    /** `yyyy-MM-dd`, or blank: a date the file has but that is not one shows blank. */
    purchaseDate: string;
    expiryDate: string;
    saleDueDate: string;
    supplierKey: string | null;
};

/** Every field but the axes, which are keyed by type. */
export type RowDrawerField = Exclude<keyof RowDrawerForm, 'axisValues'>;

/** The user's changes since the drawer opened: what Save writes, and nothing else. */
export type RowDrawerEdits = {
    fields: Partial<Pick<RowDrawerForm, RowDrawerField>>;
    /** Axis value keys the user picked, by attribute type id. */
    axisValues: Readonly<Record<string, string | null>>;
};

export const NO_EDITS: RowDrawerEdits = { fields: {}, axisValues: {} };

/**
 * The product's fields. The server reads one product per SKU, so Save writes
 * them to every row of the group (`updateGroup`).
 */
export const GROUP_FIELDS = [
    'name',
    'categoryKey',
    'brandKey',
    'unit',
    'vatType',
] as const satisfies readonly RowDrawerField[];

/**
 * The variant's and the batch's fields (the axes too). Save writes them to
 * this row alone (`updateLine`).
 */
export const LINE_FIELDS = [
    'barcode',
    'salePrice',
    'wholesalePrice',
    'minimumQuantity',
    'lotNumber',
    'quantity',
    'cost',
    'purchaseDate',
    'expiryDate',
    'saleDueDate',
    'supplierKey',
] as const satisfies readonly RowDrawerField[];

/** The fields typed as numbers: decimal GEL, or whole units for quantities. */
export const NUMBER_FIELDS = [
    'salePrice',
    'wholesalePrice',
    'minimumQuantity',
    'quantity',
    'cost',
] as const satisfies readonly RowDrawerField[];

export type RowDrawerNumberField = (typeof NUMBER_FIELDS)[number];

/** A number field whose text is not a number; Save waits until it is one or blank. */
export type RowDrawerFieldErrors = Partial<Record<RowDrawerNumberField, true>>;

/** The units the Unit select offers: the template's, `ProductUnit` names. */
export const ROW_DRAWER_UNITS: readonly ProductUnit[] = TEMPLATE_UNITS;

/** The two VAT treatments the VAT select offers. */
export const ROW_DRAWER_VAT_TYPES: readonly CatalogImportVatType[] = [
    'standard',
    'exempt',
];

/**
 * What the drawer reads beyond the row: the attribute dictionary (each
 * category's axes and their values) and the last preview's names with the
 * Names step's decisions (what each text of the file resolves to).
 */
export type RowDrawerContext = {
    attributeTypes: readonly AttributeType[];
    names: CatalogImportNamesResult | null;
    decisions: NamesDecisions;
};

/** One variation axis of the row's category, with the values it offers. */
export type RowDrawerAxis = {
    attributeTypeId: string;
    name: string;
    /** The type's values, then the file's texts the import would create (`isFileText`). */
    options: NameOption[];
};

/** Guards a decision loop; the Names step never sends one deeper than two. */
const MAX_DECISION_DEPTH = 8;

const TEXT_KEY_PREFIX = 'text:';

const trimmed = (value: string | null | undefined): string =>
    value?.trim() ?? '';

const blankToNull = (value: string): string | null => {
    const text = value.trim();
    return text === '' ? null : text;
};

const findEntry = (
    entries: readonly CatalogImportNameResult[],
    text: string,
): CatalogImportNameResult | undefined => {
    const wanted = comparableText(text);
    return entries.find((entry) => comparableText(entry.text) === wanted);
};

/**
 * A brand, category or supplier of a line as a picker key: the line's id when
 * it has one; else its text, which is the entity the last preview resolved it
 * to (`resolvedId`: an existing name, or a typo the owner sent to an existing
 * one) or, for a name the import will create, `text:` and the text. Blank is
 * none.
 */
export const entityKeyOf = (
    id: string | null | undefined,
    text: string | null | undefined,
    entries: readonly CatalogImportNameResult[],
): string | null => {
    const lineId = trimmed(id);
    if (lineId !== '') {
        return lineId;
    }
    const lineText = trimmed(text);
    if (lineText === '') {
        return null;
    }
    return (
        findEntry(entries, lineText)?.resolvedId ??
        targetKeyOf({ text: lineText })
    );
};

/** A picker key back on the line: an id, or a text for the names step; none clears both. */
const entityPatch = <IdField extends string, TextField extends string>(
    key: string | null,
    idField: IdField,
    textField: TextField,
): Record<IdField | TextField, string | null> => {
    const target = key === null ? null : targetOfKey(key);
    return {
        [idField]: target !== null && 'id' in target ? target.id : null,
        [textField]: target !== null && 'text' in target ? target.text : null,
    } as Record<IdField | TextField, string | null>;
};

/** The line's unit as the select's key: blank is Piece, as the server reads it. */
export const unitKeyOf = (unit: string | null | undefined): string => {
    const text = trimmed(unit);
    if (text === '') {
        return 'Piece';
    }
    return (
        ROW_DRAWER_UNITS.find(
            (known) => known.toLowerCase() === text.toLowerCase(),
        ) ?? text
    );
};

/** The line's VAT as the select's key: blank is standard, as the server reads it. */
export const vatKeyOf = (vatType: string | null | undefined): string => {
    const text = trimmed(vatType);
    if (text === '') {
        return 'standard';
    }
    return (
        ROW_DRAWER_VAT_TYPES.find((known) => known === text.toLowerCase()) ??
        text
    );
};

const numberText = (value: number | null | undefined): string =>
    typeof value === 'number' && Number.isFinite(value) ? String(value) : '';

/**
 * A number field as the form shows it: the line's number, else the text of
 * a cell the parse could not read (`unreadable`), so the owner sees what
 * the file has and corrects it there.
 */
const numberFieldText = (
    line: ParsedLine,
    field: RowDrawerNumberField,
): string => line.unreadable?.[field] ?? numberText(line[field]);

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A date the field can show: the line's `yyyy-MM-dd`, else blank (the problem card names what the file has). */
const dateText = (value: string | null | undefined): string => {
    const text = trimmed(value);
    return ISO_DATE.test(text) ? text : '';
};

/** A number field's text read as the server wants it. */
export type ParsedNumber = { ok: true; value: number | null } | { ok: false };

const NUMBER_TEXT = /^-?(\d+\.?\d*|\.\d+)$/;

/**
 * A number as typed: blank is none; spaces (thousands, no-break ones too)
 * are dropped and a lone comma is the decimal separator (`12,50`), as
 * Georgian writes it. Anything else is not a number. Whole units and signs are the server's to check
 * (`QUANTITY_NOT_WHOLE`, `AMOUNT_NEGATIVE`), so it gets the value as typed.
 */
export const parseNumberText = (text: string): ParsedNumber => {
    let value = text.replace(/\s/g, '');
    if (value === '') {
        return { ok: true, value: null };
    }
    if (!value.includes('.') && value.split(',').length === 2) {
        value = value.replace(',', '.');
    }
    if (!NUMBER_TEXT.test(value)) {
        return { ok: false };
    }
    return { ok: true, value: Number(value) };
};

/** The number fields whose text is not a number. */
export const fieldErrorsOf = (form: RowDrawerForm): RowDrawerFieldErrors => {
    const errors: RowDrawerFieldErrors = {};
    for (const field of NUMBER_FIELDS) {
        if (!parseNumberText(form[field]).ok) {
            errors[field] = true;
        }
    }
    return errors;
};

// ---- Axes --------------------------------------------------------------------

const isEntryFor = (
    entry: CatalogImportOptionValueRequest,
    type: Pick<AttributeType, 'id' | 'name'>,
): boolean =>
    trimmed(entry.attributeTypeId) === type.id ||
    (trimmed(entry.attributeTypeName) !== '' &&
        comparableText(entry.attributeTypeName ?? '') ===
            comparableText(type.name));

const hasValue = (entry: CatalogImportOptionValueRequest): boolean =>
    trimmed(entry.attributeValueId) !== '' || trimmed(entry.valueText) !== '';

/** A type's value whose name or alias is the text, as the server matches a cell. */
const valueIdByText = (type: AttributeType, text: string): string | null => {
    const wanted = comparableText(text);
    const value = type.values.find(
        (candidate) =>
            comparableText(candidate.name) === wanted ||
            candidate.aliases.some(
                (alias) => comparableText(alias.alias) === wanted,
            ),
    );
    return value?.id ?? null;
};

/**
 * The row's value for one axis as the select's key: the value the line names
 * by id; else the one the last check matched its text to; else, unanswered,
 * the type's value of that name or alias; else `text:` and the text, a value
 * the import would create. `null` when the line has no value for the axis
 * (the server then fills "Unspecified" and warns).
 */
export const axisKeyOf = (
    line: ParsedLine,
    result: CatalogImportLineResult | null,
    type: AttributeType,
): string | null => {
    const entry = (line.optionValues ?? []).find(
        (option) => isEntryFor(option, type) && hasValue(option),
    );
    if (!entry) {
        return null;
    }
    const valueId = trimmed(entry.attributeValueId);
    if (valueId !== '') {
        return valueId;
    }
    const text = trimmed(entry.valueText);
    const resolved = result?.optionValues.find(
        (option) => option.attributeTypeId === type.id,
    );
    if (resolved) {
        return resolved.attributeValueId ?? targetKeyOf({ text });
    }
    return valueIdByText(type, text) ?? targetKeyOf({ text });
};

/** The axes an existing category has, plus those the Names step adds to it. */
const existingAxisIds = (
    categoryId: string,
    context: RowDrawerContext,
): Set<string> => {
    const ids = new Set(
        context.attributeTypes
            .filter((type) =>
                (type.variationCategories ?? []).some(
                    (link) => link.categoryId === categoryId,
                ),
            )
            .map((type) => type.id),
    );
    for (const entry of context.names?.categories ?? []) {
        if (entry.resolvedId === categoryId && entry.addAxes) {
            entry.axesToAdd.forEach((axis) => ids.add(axis.attributeTypeId));
        }
    }
    return ids;
};

/** The axes of a category the file names by text, following the Names step's decisions. */
const textAxisIds = (
    text: string,
    context: RowDrawerContext,
    depth: number,
): Set<string> => {
    const entry = findEntry(context.names?.categories ?? [], text);
    if (entry?.resolvedId) {
        return existingAxisIds(entry.resolvedId, context);
    }
    const decision = context.decisions.categories.find(
        (candidate) => comparableText(candidate.text) === comparableText(text),
    );
    const target = decision?.action === 'useExisting' ? decision.target : null;
    if (target && depth < MAX_DECISION_DEPTH) {
        return 'id' in target
            ? existingAxisIds(target.id, context)
            : textAxisIds(target.text, context, depth + 1);
    }
    // A category the import creates takes the axes its rows use, unless the
    // owner unticked "add axes".
    if (entry && entry.addAxes) {
        return new Set(entry.axesToAdd.map((axis) => axis.attributeTypeId));
    }
    return new Set();
};

/**
 * The variation axes of the category a key names, in the dictionary's order:
 * an existing category's own axes and those the Names step adds to it; for a
 * category the import creates, the axes its rows use (when the owner kept
 * "add axes"). No category, no axes, so a charger shows no attribute selects.
 */
export const categoryAxesOf = (
    categoryKey: string | null,
    context: RowDrawerContext,
): AttributeType[] => {
    if (categoryKey === null) {
        return [];
    }
    const target = targetOfKey(categoryKey);
    if (target === null) {
        return [];
    }
    const ids =
        'id' in target
            ? existingAxisIds(target.id, context)
            : textAxisIds(target.text, context, 0);
    return context.attributeTypes.filter((type) => ids.has(type.id));
};

/**
 * Per attribute type, the value texts of the file the import would create
 * (the last check matched no value to them), so another row's new value
 * stays selectable.
 */
export const newValueTextsOf = (
    lines: readonly CatalogImportLineResult[],
): ReadonlyMap<string, readonly string[]> => {
    const byType = new Map<string, Map<string, string>>();
    for (const line of lines) {
        for (const option of line.optionValues) {
            const text = trimmed(option.valueText);
            if (option.attributeValueId !== null || text === '') {
                continue;
            }
            const texts = byType.get(option.attributeTypeId) ?? new Map();
            if (!texts.has(comparableText(text))) {
                texts.set(comparableText(text), text);
            }
            byType.set(option.attributeTypeId, texts);
        }
    }
    return new Map(
        [...byType].map(([typeId, texts]) => [typeId, [...texts.values()]]),
    );
};

const fileTextOption = (text: string): NameOption => ({
    id: `${TEXT_KEY_PREFIX}${text}`,
    label: text,
    isFileText: true,
});

/** Adds the key's own option when the list lacks it (a file text no other row has). */
const withKeyOption = (
    options: NameOption[],
    key: string | null,
): NameOption[] => {
    if (key === null || options.some((option) => option.id === key)) {
        return options;
    }
    const target = targetOfKey(key);
    return target !== null && 'text' in target
        ? [...options, fileTextOption(target.text)]
        : options;
};

/**
 * An axis select's options: the type's values in their order, then the
 * file's new texts for it, then the row's own value when it is neither.
 */
export const axisOptionsOf = (
    type: AttributeType,
    newTexts: ReadonlyMap<string, readonly string[]>,
    currentKey: string | null,
): NameOption[] => {
    const values = [...type.values]
        .sort((a, b) => a.position - b.position)
        .map((value) => ({ id: value.id, label: value.name }));
    const texts = (newTexts.get(type.id) ?? []).map(fileTextOption);
    return withKeyOption([...values, ...texts], currentKey);
};

/**
 * A brand, category or supplier picker's options: the catalog's entities
 * (`entityOptions`, from the Names step's option builders), then the file's
 * names the import would create (no `resolvedId`), then the row's own text
 * when the last check had no entry for it.
 */
export const pickerOptionsOf = (
    entityOptions: readonly NameOption[],
    entries: readonly CatalogImportNameResult[],
    currentKey: string | null,
): NameOption[] => {
    const texts = entries
        .filter((entry) => entry.resolvedId === null)
        .map((entry) => fileTextOption(entry.text.trim()));
    return withKeyOption([...entityOptions, ...texts], currentKey);
};

// ---- The form and what Save writes ----------------------------------------------

/** The form as the row is: the line's values, read through the last check. */
export const initialFormOf = (
    line: ParsedLine,
    result: CatalogImportLineResult | null,
    context: RowDrawerContext,
): RowDrawerForm => {
    const names = context.names;
    const categoryKey = entityKeyOf(
        line.categoryId,
        line.categoryText,
        names?.categories ?? [],
    );
    return {
        name: trimmed(line.name),
        categoryKey,
        brandKey: entityKeyOf(
            line.brandId,
            line.brandText,
            names?.brands ?? [],
        ),
        unit: unitKeyOf(line.unit),
        vatType: vatKeyOf(line.vatType),
        barcode: trimmed(line.barcode),
        axisValues: Object.fromEntries(
            categoryAxesOf(categoryKey, context).map((type) => [
                type.id,
                axisKeyOf(line, result, type),
            ]),
        ),
        salePrice: numberFieldText(line, 'salePrice'),
        wholesalePrice: numberFieldText(line, 'wholesalePrice'),
        minimumQuantity: numberFieldText(line, 'minimumQuantity'),
        lotNumber: trimmed(line.lotNumber),
        quantity: numberFieldText(line, 'quantity'),
        cost: numberFieldText(line, 'cost'),
        purchaseDate: dateText(line.purchaseDate),
        expiryDate: dateText(line.expiryDate),
        saleDueDate: dateText(line.saleDueDate),
        supplierKey: entityKeyOf(
            line.supplierId,
            line.supplierText,
            names?.suppliers ?? [],
        ),
    };
};

/**
 * The form with the user's edits on top. The axes follow the category the
 * form shows: changing the category shows the new one's axes, each with the
 * row's value for it, or the user's pick.
 */
export const formOf = (
    line: ParsedLine,
    result: CatalogImportLineResult | null,
    context: RowDrawerContext,
    edits: RowDrawerEdits,
): RowDrawerForm => {
    const form = { ...initialFormOf(line, result, context), ...edits.fields };
    const axisValues: Record<string, string | null> = {};
    for (const type of categoryAxesOf(form.categoryKey, context)) {
        axisValues[type.id] =
            type.id in edits.axisValues
                ? (edits.axisValues[type.id] ?? null)
                : axisKeyOf(line, result, type);
    }
    return { ...form, axisValues };
};

/** One axis value as a request option: by id (the name kept for the table), or a text to create. */
const optionOf = (
    type: AttributeType,
    key: string,
): CatalogImportOptionValueRequest => {
    const target = targetOfKey(key);
    if (target !== null && 'id' in target) {
        return {
            attributeTypeId: type.id,
            attributeTypeName: type.name,
            attributeValueId: target.id,
            valueText:
                type.values.find((value) => value.id === target.id)?.name ??
                null,
        };
    }
    return {
        attributeTypeId: type.id,
        attributeTypeName: type.name,
        valueText: target?.text ?? null,
    };
};

/**
 * The line's options with some axes set anew: each changed axis's entry
 * takes the place of the line's first one for that type (the server reads
 * only the first), an axis set to none is dropped, and the line's other
 * entries stay as they were, so a value of a type that is no axis of the
 * category keeps its problem until the category or the file changes.
 */
export const optionValuesWith = (
    options: readonly CatalogImportOptionValueRequest[],
    changes: readonly { type: AttributeType; key: string | null }[],
): CatalogImportOptionValueRequest[] => {
    const placed = new Set<string>();
    const next: CatalogImportOptionValueRequest[] = [];
    for (const option of options) {
        const change = changes.find(({ type }) => isEntryFor(option, type));
        if (!change) {
            next.push(option);
            continue;
        }
        if (!placed.has(change.type.id)) {
            placed.add(change.type.id);
            if (change.key !== null) {
                next.push(optionOf(change.type, change.key));
            }
        }
    }
    for (const { type, key } of changes) {
        if (!placed.has(type.id) && key !== null) {
            next.push(optionOf(type, key));
        }
    }
    return next;
};

/** What Save writes: the product's fields to the group, the rest to the row. */
export type RowDrawerPatches = {
    group: ParsedLinePatch;
    line: ParsedLinePatch;
};

const numberValue = (text: string): number | null => {
    const parsed = parseNumberText(text);
    return parsed.ok ? parsed.value : null;
};

const isNumberField = (field: RowDrawerField): field is RowDrawerNumberField =>
    (NUMBER_FIELDS as readonly RowDrawerField[]).includes(field);

const DATE_FIELDS = [
    'purchaseDate',
    'expiryDate',
    'saleDueDate',
] as const satisfies readonly RowDrawerField[];

type RowDrawerDateField = (typeof DATE_FIELDS)[number];

const isDateField = (field: RowDrawerField): field is RowDrawerDateField =>
    (DATE_FIELDS as readonly RowDrawerField[]).includes(field);

/** A field's value as Save would write it, to tell a real change from retyping. */
const writtenValue = (
    field: RowDrawerField,
    form: RowDrawerForm,
): string | number | null => {
    if (isNumberField(field)) {
        return numberValue(form[field]);
    }
    const value = form[field];
    return typeof value === 'string' ? value.trim() : value;
};

/** One changed field on the line, in the request's terms. */
const fieldPatch = (
    field: RowDrawerField,
    form: RowDrawerForm,
): ParsedLinePatch => {
    switch (field) {
        case 'name':
            return { name: blankToNull(form.name) };
        case 'categoryKey':
            return entityPatch(form.categoryKey, 'categoryId', 'categoryText');
        case 'brandKey':
            return entityPatch(form.brandKey, 'brandId', 'brandText');
        case 'supplierKey':
            return entityPatch(form.supplierKey, 'supplierId', 'supplierText');
        case 'unit':
            return { unit: form.unit };
        case 'vatType':
            return { vatType: form.vatType };
        case 'barcode':
            return { barcode: blankToNull(form.barcode) };
        case 'lotNumber':
            return { lotNumber: blankToNull(form.lotNumber) };
        case 'salePrice':
        case 'wholesalePrice':
        case 'minimumQuantity':
        case 'quantity':
        case 'cost':
            return { [field]: numberValue(form[field]) };
        case 'purchaseDate':
        case 'expiryDate':
        case 'saleDueDate':
            return { [field]: blankToNull(form[field]) };
    }
};

/**
 * What an edited field is compared with: what Save would have written for
 * the row as it is. A date is compared with what the line holds instead of
 * what the field shows, since a date the file has but that is not one shows
 * blank: clearing it is a change, and it writes the clear.
 */
const baselineValue = (
    field: RowDrawerField,
    line: ParsedLine,
    initial: RowDrawerForm,
): string | number | null =>
    isDateField(field) ? trimmed(line[field]) : writtenValue(field, initial);

/** The number fields whose cell the parse could not read (`unreadable`). */
const unreadableFieldsOf = (line: ParsedLine): RowDrawerNumberField[] =>
    NUMBER_FIELDS.filter((field) => line.unreadable?.[field] !== undefined);

/**
 * The patches of the fields the user changed, plus, `withUnreadable`, of
 * every number field whose cell the parse could not read: the field shows
 * that text, so Save writes what it holds then (a number, or a blank) and
 * drops the marks, which ends the row's `CELL_NOT_A_NUMBER` problems.
 */
const patchesFor = (
    line: ParsedLine,
    result: CatalogImportLineResult | null,
    context: RowDrawerContext,
    edits: RowDrawerEdits,
    withUnreadable: boolean,
): RowDrawerPatches => {
    const initial = initialFormOf(line, result, context);
    const form = formOf(line, result, context, edits);
    const unreadable: ReadonlySet<RowDrawerField> = new Set(
        withUnreadable ? unreadableFieldsOf(line) : [],
    );
    const changed = (field: RowDrawerField): boolean =>
        unreadable.has(field) ||
        (field in edits.fields &&
            writtenValue(field, form) !== baselineValue(field, line, initial));

    const group: ParsedLinePatch = {};
    for (const field of GROUP_FIELDS) {
        if (changed(field)) Object.assign(group, fieldPatch(field, form));
    }
    const rowPatch: ParsedLinePatch = {};
    for (const field of LINE_FIELDS) {
        if (changed(field)) Object.assign(rowPatch, fieldPatch(field, form));
    }

    const axisChanges = categoryAxesOf(form.categoryKey, context)
        .filter(
            (type) =>
                type.id in edits.axisValues &&
                form.axisValues[type.id] !== axisKeyOf(line, result, type),
        )
        .map((type) => ({ type, key: form.axisValues[type.id] ?? null }));
    if (axisChanges.length > 0) {
        rowPatch.optionValues = optionValuesWith(
            line.optionValues ?? [],
            axisChanges,
        );
    }
    if (unreadable.size > 0) {
        rowPatch.unreadable = undefined;
    }

    return { group, line: rowPatch };
};

/**
 * What Save writes, from the fields the user changed and nothing else: a
 * field set back to what it was (`1.0` for `1`, a name with a trailing
 * space) writes nothing, so an untouched sibling keeps its own value and an
 * unreadable date the drawer shows blank stays as typed until the user
 * edits that field (clearing it writes the clear). A number cell the parse
 * could not read is the exception: its field shows the text, so Save writes
 * what the field holds and the row's problem for it ends.
 * Product fields go to the group (`group`), variant and batch fields and the
 * axes to this row (`line`).
 */
export const patchesOf = (
    line: ParsedLine,
    result: CatalogImportLineResult | null,
    context: RowDrawerContext,
    edits: RowDrawerEdits,
): RowDrawerPatches => patchesFor(line, result, context, edits, true);

export const isEmptyPatch = (patch: ParsedLinePatch): boolean =>
    Object.keys(patch).length === 0;

/**
 * Whether closing the drawer would lose something the user did: Save would
 * write a change the user made, or a number field the user typed in holds
 * text that is not a number yet. A field set back to what it was is not a
 * change, and a cell the drawer shows as it came from the file is not one
 * either, however it reads.
 */
export const hasUnsavedEdits = (
    line: ParsedLine,
    result: CatalogImportLineResult | null,
    context: RowDrawerContext,
    edits: RowDrawerEdits,
): boolean => {
    const patches = patchesFor(line, result, context, edits, false);
    if (!isEmptyPatch(patches.group) || !isEmptyPatch(patches.line)) {
        return true;
    }
    return NUMBER_FIELDS.some((field) => {
        const typed = edits.fields[field];
        return typed !== undefined && !parseNumberText(typed).ok;
    });
};
